import { useMessage } from "hooks/intl";
import {
  Access,
  Permission,
  PermissionCategory,
  PermissionItem,
} from "types/role";

import { Divider, Grid, Typography } from "@mui/material";
import { hasModifyAccessToResource } from "api/general";
import { Search } from "components/FormControls/Select/Search";
import { OLSwitch } from "components/FormControls/Toggle";
import { Tooltip } from "components/Tooltips/Tooltip";
import { ExternalLink } from "ExternalLink";
import { FieldArray } from "formik";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { permissions } from "./helpers";
import { useStyles } from "./RoleForm.styles";

export function Permissions({ isFormDataReady }: { isFormDataReady: boolean }) {
  const classes = useStyles();
  const { message } = useMessage();

  const [searchedValue, setSearchedValue] = useState("");

  const hasModifyRolePermission = hasModifyAccessToResource("ACL");

  const { VIEW, MODIFY } = Access;

  const divider = (
    <Divider flexItem orientation="vertical" className={classes.divider} />
  );

  const filterPermissions = (
    permissions: PermissionCategory[],
    searchQuery: string,
  ) => {
    return permissions
      ?.map((permission) => {
        const filteredPermissions = permission.items.filter(
          (item) =>
            message({ id: item.name })
              .toLocaleLowerCase()
              .includes(searchQuery.toLocaleLowerCase()) ||
            message({ id: item.description })
              .toLocaleLowerCase()
              .includes(searchQuery.toLocaleLowerCase()),
        );
        return { ...permission, items: filteredPermissions };
      })
      .filter((permission) => permission.items.length > 0);
  };

  const searchedPermissionsList = filterPermissions(permissions, searchedValue);

  return (
    <div className={classes.permissionsForm}>
      <Grid container className={classes.permissionsFormTitle}>
        <Grid item>
          <Typography className={classes.permissionsTitle}>
            {message({ id: "permissions" })}
          </Typography>
          {isFormDataReady && (
            <Typography className={classes.permissionsSubtitle}>
              {message(
                { id: "permissionsInfo" },
                {
                  learnMore: (
                    <ExternalLink path="https://help.openloyalty.io/main-features/settings/roles">
                      {message({ id: "learnMore" })}
                    </ExternalLink>
                  ),
                },
              )}
            </Typography>
          )}
        </Grid>
        {isFormDataReady && (
          <Grid item>
            <Search
              searchedValue={searchedValue}
              setSearchedValue={setSearchedValue}
              className={classes.searchInput}
              placeholder={message({ id: "searchForResources" })}
            />
          </Grid>
        )}
      </Grid>
      {isFormDataReady ? (
        <div className={classes.permissionsContainer}>
          <Grid container className={classes.permissionsHeader}>
            <Grid item className={classes.permissionsHeaderTitle}>
              {message({ id: "resource" })}
            </Grid>
            {divider}
            <Grid item className={classes.permissionsHeaderAccess}>
              {message({ id: "view" })}
            </Grid>
            {divider}
            <Grid item className={classes.permissionsHeaderAccess}>
              {message({ id: "modify" })}
            </Grid>
          </Grid>
          {searchedPermissionsList.length > 0 ? (
            <FieldArray
              name="permissions"
              render={({ push, form: { values, setFieldValue } }) => {
                return (
                  <Grid container>
                    {searchedPermissionsList.map((permission, index) => (
                      <>
                        {permission.category && (
                          <Grid
                            item
                            xs={12}
                            className={classes.permissionCategory}
                          >
                            {message({ id: permission.category })}
                          </Grid>
                        )}
                        {permission?.items?.map(
                          (item: PermissionItem, itemIndex) => {
                            // Filtering is necessary because previously defined perimssions
                            // can have several permissions with the same code and diference access level
                            const permissionsWithTheSameCode =
                              values?.permissions.filter(
                                (permission: Permission) =>
                                  permission?.resource === item.code,
                              );

                            const isModifyAccessChecked =
                              permissionsWithTheSameCode?.find(
                                (permission: Permission) =>
                                  permission?.access === MODIFY,
                              );

                            const isViewAccessChecked =
                              values?.permissions?.find(
                                (permission: Permission) =>
                                  permission?.resource === item.code,
                              );

                            const changePermissionAccess = (
                              access: `${Access}`,
                            ) =>
                              setFieldValue(
                                "permissions",
                                values?.permissions?.map(
                                  (permission: Permission) =>
                                    permission?.resource === item.code
                                      ? {
                                          resource: item.code,
                                          access,
                                        }
                                      : permission,
                                ),
                              );

                            const removePermission = () =>
                              setFieldValue(
                                "permissions",
                                values?.permissions?.filter(
                                  (permission: Permission) =>
                                    permission?.resource !== item.code,
                                ),
                              );
                            return (
                              <>
                                <Grid item className={classes.permission}>
                                  <p className={classes.permissionName}>
                                    {message({ id: item.name })}
                                  </p>
                                  {item.description && (
                                    <p className={classes.permissionDesc}>
                                      {message({ id: item.description })}
                                    </p>
                                  )}
                                </Grid>
                                {divider}
                                <Grid item className={classes.permissionAccess}>
                                  <Tooltip
                                    placement="top"
                                    text={message({
                                      id: "permissionTooltip",
                                    })}
                                    disableHoverListener={
                                      !isModifyAccessChecked
                                    }
                                    maxWidth="330px"
                                  >
                                    <div>
                                      <OLSwitch
                                        disabled={
                                          !!isModifyAccessChecked ||
                                          !hasModifyRolePermission
                                        }
                                        checked={!!isViewAccessChecked}
                                        onChange={(e) =>
                                          e.target.checked
                                            ? push({
                                                access: VIEW,
                                                resource: item.code,
                                              })
                                            : removePermission()
                                        }
                                        data-test-id={`view-access-${index}-${itemIndex}`}
                                      />
                                    </div>
                                  </Tooltip>
                                </Grid>
                                {divider}
                                <Grid item className={classes.permissionAccess}>
                                  <OLSwitch
                                    disabled={!hasModifyRolePermission}
                                    checked={!!isModifyAccessChecked}
                                    onChange={(e) => {
                                      const isAlreadyCheckedAsView =
                                        values?.permissions?.find(
                                          (permission: Permission) =>
                                            permission?.resource === item.code,
                                        )?.access === VIEW;

                                      if (e.target.checked) {
                                        isAlreadyCheckedAsView
                                          ? changePermissionAccess(MODIFY)
                                          : push({
                                              access: MODIFY,
                                              resource: item.code,
                                            });
                                      } else {
                                        changePermissionAccess(VIEW);
                                      }
                                    }}
                                    data-test-id={`modify-access-${index}-${itemIndex}`}
                                  />
                                </Grid>
                              </>
                            );
                          },
                        )}
                      </>
                    ))}
                  </Grid>
                );
              }}
            />
          ) : (
            <div>
              <Typography className={classes.noDataInfo}>
                {message({ id: "noResultsFound" })}
              </Typography>
            </div>
          )}
        </div>
      ) : (
        <Skeleton height={100} />
      )}
    </div>
  );
}
